import React from 'react';
import {
  FaGithub,
  FaExternalLinkAlt,
  FaArrowRight,
  FaEye,
  FaMobileAlt,
  FaDatabase,
  FaProjectDiagram,
  FaLeaf,
  FaGraduationCap,
} from 'react-icons/fa';
import { FadeIn, HoverCard } from './animations/MotionPrimitives';
import SectionCourier from './illustrations/SectionCourier';
import EventShieldPreview from './illustrations/EventShieldPreview';
import ModelAnalyticsPreview from './illustrations/ModelAnalyticsPreview';
import citySipShot from './assets/city-sip.png';
import aiDashboardShot from './assets/ai-industry-dashboard.png';
import { SOCIALS } from './Navigation';
import './styles/Projects.css';

/*
 * Featured projects. Every link, name and technology is real and preserved.
 * Two projects ship real product captures (`screenshot`). The two that are not
 * publicly deployed yet use an original concept-preview illustration (`Preview`)
 * that is labelled as such, never presented as a real screenshot.
 * All four cards share one structure and render in a single 2-column grid.
 */
const FEATURED = [
  {
    title: 'CitySip',
    category: 'Full-Stack Web App',
    tone: 'blue',
    value: 'Discover happy hours, local deals and after-work events across 15 U.S. cities.',
    description:
      'An interactive map surfaces nearby venues with live deal countdowns. Natural-language search turns everyday phrasing into filtered results.',
    contribution:
      'I built the Next.js and TypeScript app, the map and search experience, and the Prisma/PostgreSQL layer behind venue and event imports.',
    tags: ['Next.js', 'TypeScript', 'Prisma', 'PostgreSQL', 'Anthropic API'],
    github: 'https://github.com/AdibaAdi/citysip',
    demo: 'https://citysip-theta.vercel.app',
    screenshot: citySipShot,
    /* 1325x846 capture in a 16:9 slot: anchor top so the product name, headline
       and the live "Happening now" venue list stay intact. */
    objectPosition: 'center top',
    alt:
      'CitySip home page with the headline "Find the best happy hours in your city, before they end" beside a Happening Now list of venues with live deal countdowns',
  },
  {
    title: 'EventShield',
    category: 'Developer Tooling',
    tone: 'lav',
    value: 'Capture failed events, diagnose issues and guide developers toward remediation.',
    description:
      'Express middleware records failed API and webhook payloads, headers and error traces. LLM-assisted diagnosis then surfaces the likely cause and a remediation step.',
    contribution:
      'I built the capture middleware, the error and trace history store, and the LLM-assisted diagnosis workflow.',
    tags: ['TypeScript', 'Express', 'REST APIs', 'Webhooks', 'LLM Integration'],
    github: 'https://github.com/AdibaAdi/EventShield-Agent',
    demo: null,
    Preview: EventShieldPreview,
  },
  {
    title: 'AI Industry Intelligence Dashboard',
    category: 'AI Analytics',
    tone: 'gold',
    value: 'Explore AI market signals through structured analytics and AI-assisted research.',
    description:
      'Tracks growth, influence and market signals across 100+ AI companies through REST and OpenAI APIs. Structured outputs and evaluation workflows keep the generated insights reviewable.',
    contribution:
      'I built the Python data and API layer, the OpenAI integration, and the structured-output and evaluation workflows.',
    tags: ['Python', 'React', 'OpenAI APIs', 'REST APIs', 'RAG'],
    github: 'https://github.com/AdibaAdi/ai-industry-dashboard',
    demo: 'https://ai-industry-dashboard.netlify.app/',
    screenshot: aiDashboardShot,
    /* 1343x911 capture in a 16:9 slot: anchor top so the dashboard title, side
       navigation, scorecards and both analytics panels stay readable. */
    objectPosition: 'center top',
    alt:
      'AI Industry Dashboard showing side navigation, scorecards for tracked companies and top domain, a company growth trend line chart and a domain distribution donut chart',
  },
  {
    title: 'Hugging Face Model Analytics Agent',
    category: 'AI Data Tool',
    tone: 'sage',
    value: 'Ask natural-language questions about model activity and receive tables, charts and forecasts.',
    description:
      'A Streamlit agent turns plain-language questions into queries over Hugging Face model data. Answers come back as summaries, tables, charts and forecasts.',
    contribution:
      'I built the LangGraph agent, the Hugging Face API and PostgreSQL data layer, and the table, chart and forecasting outputs.',
    tags: ['Python', 'Streamlit', 'LangGraph', 'PostgreSQL', 'Hugging Face API'],
    github: 'https://github.com/AdibaAdi/huggingface-agentic-analytics',
    demo: null,
    Preview: ModelAnalyticsPreview,
  },
];

const MORE = [
  {
    title: 'VisWalk AI',
    category: 'Computer Vision',
    tone: 'blue',
    icon: <FaEye aria-hidden="true" />,
    description:
      'Real-time assistive navigation that detects obstacles and guides visually impaired users with voice feedback.',
    tags: ['Python', 'YOLOv5', 'Computer Vision'],
    github: 'https://github.com/AdibaAdi/VisWalk-AI',
  },
  {
    title: 'MedBuzz',
    category: 'Mobile App',
    tone: 'blush',
    icon: <FaMobileAlt aria-hidden="true" />,
    description:
      'Mobile healthcare hiring app with profile onboarding, ATS-ready applications and recruiter messaging.',
    tags: ['React Native', 'TypeScript', 'Node.js'],
    github: 'https://github.com/JaskirtKaler/Medbuzz',
  },
  {
    title: 'SymTrack API',
    category: 'Backend API',
    tone: 'sage',
    icon: <FaDatabase aria-hidden="true" />,
    description:
      'Health-analytics backend that tracks chronic-condition data, generates alerts and powers clinical insight workflows.',
    tags: ['FastAPI', 'PostgreSQL', 'REST API'],
    github: 'https://github.com/AdibaAdi/symtrack-api',
  },
  {
    title: 'Cropchain',
    category: 'Multi-Agent AI',
    tone: 'lav',
    icon: <FaProjectDiagram aria-hidden="true" />,
    description:
      'Multi-agent AI system that models the software delivery lifecycle from requirements to design and testing.',
    tags: ['Python', 'AutoGen', 'Multi-Agent AI'],
    github: 'https://github.com/AdibaAdi/Cropchain',
  },
  {
    title: 'SmartPantry',
    category: 'Full-Stack App',
    tone: 'gold',
    icon: <FaLeaf aria-hidden="true" />,
    description:
      'Meal-planning app with recommendation workflows that cut food waste and weekly planning friction.',
    tags: ['React', 'MongoDB', 'Node.js'],
    github: 'https://github.com/AdibaAdi/SmartPantry-MealPlanning',
  },
  {
    title: 'Scholarly',
    category: 'AI Education',
    tone: 'blue',
    icon: <FaGraduationCap aria-hidden="true" />,
    description:
      'Scholarship-matching platform that ranks profiles to opportunities using the OpenAI API.',
    tags: ['Python', 'OpenAI API', 'Flask'],
    github: 'https://github.com/AdibaAdi/AI_hackathon-Scholarly-App',
  },
];

/* One media slot for all four cards: identical frame, identical reserved 16:9
   area. It renders either a real product capture or an original concept
   preview, and the concept previews always carry a visible honest label. */
function ProjectVisual({ project }) {
  const { Preview } = project;

  return (
    <div className="shot-frame">
      <div className="shot-bar">
        <span className="shot-dots" aria-hidden="true">
          <span className="shot-dot" />
          <span className="shot-dot" />
          <span className="shot-dot" />
        </span>
        {Preview && <span className="shot-tag">Concept Preview</span>}
      </div>
      <div className="shot-screen">
        {Preview ? (
          <Preview />
        ) : (
          <img
            className="shot-img"
            src={project.screenshot}
            alt={project.alt}
            loading="lazy"
            width="1280"
            height="720"
            style={{ objectPosition: project.objectPosition }}
          />
        )}
      </div>
    </div>
  );
}

/* Shared by all four featured projects: same structure, same content order,
   only the copy differs. */
function FeaturedProjectCard({ project, delay }) {
  return (
    <FadeIn as="article" className="pcard" delay={delay}>
      <div className="pcard-media">
        <ProjectVisual project={project} />
      </div>

      <div className="pcard-info">
        <span className={`pcard-badge pcard-badge--${project.tone}`}>{project.category}</span>
        <h3 className="pcard-title">{project.title}</h3>
        <p className="pcard-value">{project.value}</p>
        <p className="pcard-desc">{project.description}</p>
        <p className="pcard-contribution">
          <span className="pcard-contribution-label">My contribution:</span>{' '}
          {project.contribution}
        </p>
        <div className="tag-list pcard-tags">
          {project.tags.map((tag) => (
            <span className="tag" key={tag}>
              {tag}
            </span>
          ))}
        </div>
        <div className="pcard-actions">
          {project.demo && (
            <a className="btn btn-primary" href={project.demo} target="_blank" rel="noopener noreferrer">
              <FaExternalLinkAlt aria-hidden="true" /> Live Demo
            </a>
          )}
          <a
            className={`btn ${project.demo ? 'btn-secondary' : 'btn-primary'}`}
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGithub aria-hidden="true" /> GitHub
          </a>
        </div>
      </div>
    </FadeIn>
  );
}

function Projects() {
  return (
    <section className="section projects" id="projects">
      <div className="section-inner">
        {/* the courier sits with the heading, at the edge of the project grid */}
        <FadeIn className="chapter-head projects-head">
          <div>
            <span className="chapter-mark">03</span>
            <h2 className="chapter-title">Featured Projects</h2>
          </div>
          <SectionCourier
            className="projects-courier"
            carry="letter"
            width={68}
            from={{ x: 16, y: -12, rotate: 12 }}
            restRotate={5}
          />
        </FadeIn>

        <div className="featured-grid">
          {FEATURED.map((project, index) => (
            <FeaturedProjectCard project={project} key={project.title} delay={(index % 2) * 0.06} />
          ))}
        </div>

        <FadeIn className="more-head">
          <h3 className="more-title">More Projects</h3>
        </FadeIn>

        <div className="more-grid">
          {MORE.map((project, index) => (
            <HoverCard className="more-card" key={project.title} as="article" lift={-4}>
              <FadeIn delay={(index % 3) * 0.05}>
                <a
                  className="more-card-link"
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${project.title} on GitHub`}
                >
                  <div className="more-card-head">
                    <span className={`tile tile--${project.tone}`} aria-hidden="true">
                      {project.icon}
                    </span>
                    <div className="more-card-headtext">
                      <span className="more-card-cat">{project.category}</span>
                      <h4 className="more-card-title">{project.title}</h4>
                    </div>
                    <FaGithub aria-hidden="true" className="more-card-icon" />
                  </div>
                  <p className="more-card-desc">{project.description}</p>
                  <div className="tag-list more-card-tags">
                    {project.tags.map((tag) => (
                      <span className="tag" key={tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </a>
              </FadeIn>
            </HoverCard>
          ))}
        </div>

        <FadeIn className="projects-footer">
          <a className="more-link" href={SOCIALS.github} target="_blank" rel="noopener noreferrer">
            View all on GitHub <FaArrowRight aria-hidden="true" />
          </a>
        </FadeIn>
      </div>
    </section>
  );
}

export default Projects;
